import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import classNames from '../../../utils/classNames';
import DiscoverActionButton from '../../molecules/Discover/DiscoverActionButton';
import DiscoverFilterToggle from '../../molecules/Discover/DiscoverFilterToggle';
import RefreshButton from '../../atoms/RefreshButton';
import PanelToggleButton from '../../atoms/PanelToggleButton';
import ScrollArea from '../../atoms/ScrollArea';
import { ReactComponent as AddMarkerIcon } from '../../../images/add-marker-icon.svg';
import { ReactComponent as AddLayerIcon } from '../../../images/add-layer-icon.svg';
import { ReactComponent as PencilIcon } from '../../../images/pencil.svg';
import { ReactComponent as TrashIcon } from '../../../images/trash.svg';
import './DiscoverMapPanel.scss';

/** Map object type to DiscoverFilterToggle tone. */
const TYPE_TO_TONE = { ARTIST: 'artist', BAND: 'band', EVENT: 'event', SESSION: 'session', VENUE: 'venue' };

/** Map object type to its i18n label key. */
const TYPE_TO_LABEL_KEY = {
    ARTIST: 'discoverPage.panel.artistLabel',
    BAND: 'discoverPage.panel.bandLabel',
    EVENT: 'discoverPage.panel.eventLabel',
    SESSION: 'discoverPage.panel.sessionLabel',
    VENUE: 'discoverPage.panel.venueLabel',
};

function SortableFilterRow({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        cursor: 'grab',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
}

export default function DiscoverMapPanel({
    t,
    layers,
    layerCounts = {},
    onToggleLayer,
    onEditLayer = undefined,
    onDeleteLayer = undefined,
    onLayerColorChange = undefined,
    onAddMarkerClick,
    onAddLayerClick,
    onReorderLayers = undefined,
    totalCount = undefined,
    selectedCount = 0,
    lastUpdatedMessage = '',
    onRefresh = undefined,
    isComposerOpen = false,
    composerCard = null,
    children = null,
}) {
    const [isMinimized, setIsMinimized] = useState(false);
    const hasAnyLayerEnabled = Array.isArray(layers) && layers.some((l) => l.enabled);
    const showSelected = typeof selectedCount === 'number' && selectedCount > 0;
    const collapseToggleAria = isMinimized
        ? t('discoverPage.panel.expandAria', 'Expand map controls panel')
        : t('discoverPage.panel.minimizeAria', 'Minimize map controls panel');
    const collapseToggleTitle = isMinimized
        ? t('discoverPage.panel.expand', 'Expand')
        : t('discoverPage.panel.minimize', 'Minimize');

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    const safeLayers = useMemo(() => (Array.isArray(layers) ? layers : []), [layers]);
    const counts = layerCounts || {};
    const layerIds = useMemo(() => safeLayers.map((l) => l.id), [safeLayers]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id || typeof onReorderLayers !== 'function') return;
        const oldIndex = safeLayers.findIndex((l) => l.id === active.id);
        const newIndex = safeLayers.findIndex((l) => l.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        onReorderLayers(oldIndex, newIndex);
    };

    const toggleButton = (
        <PanelToggleButton
            isMinimized={isMinimized}
            onClick={() => setIsMinimized((current) => !current)}
            ariaLabel={collapseToggleAria}
            title={collapseToggleTitle}
            className={classNames('discover-panel__toggle-button', {
                'discover-panel__toggle-button--minimized': isMinimized,
            })}
        />
    );

    const timestampSection = lastUpdatedMessage ? (
        <p className={classNames('discover-panel__timestamp', {
            'discover-panel__timestamp--minimized': isMinimized,
        })}
        >
            <span className="discover-panel__timestamp-text">{lastUpdatedMessage}</span>
            {typeof onRefresh === 'function' && (
                <span className="discover-panel__refresh-group">
                    <RefreshButton
                        onClick={onRefresh}
                        ariaLabel={t('discoverPage.panel.refreshAria', 'Refresh map data')}
                        title={t('discoverPage.panel.refresh', 'Refresh')}
                    />
                </span>
            )}
        </p>
    ) : null;

    const filtersCard = (
        <div className="discover-panel__filters-card">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={layerIds} strategy={verticalListSortingStrategy}>
                    {safeLayers.map((layer) => {
                        const count = typeof counts[layer.id] === 'number' ? counts[layer.id] : 0;
                        const layerActions = [];
                        if (typeof onEditLayer === 'function') {
                            layerActions.push({
                                icon: <PencilIcon />,
                                label: t('discoverPage.panel.editLayer', 'Edit layer'),
                                onClick: () => onEditLayer(layer.id),
                                ariaLabel: t('discoverPage.panel.editLayer', 'Edit layer'),
                            });
                        }
                        if (typeof onDeleteLayer === 'function') {
                            layerActions.push({
                                icon: <TrashIcon />,
                                label: t('discoverPage.panel.deleteLayer', 'Delete'),
                                onClick: () => onDeleteLayer(layer.id),
                                ariaLabel: t('discoverPage.panel.deleteLayer', 'Delete'),
                                tone: 'danger',
                            });
                        }
                        return (
                            <SortableFilterRow key={layer.id} id={layer.id}>
                                <DiscoverFilterToggle
                                    name={`discover-filter-${layer.id}`}
                                    label={layer.name || layer.objectType}
                                    countLabel={`${t(TYPE_TO_LABEL_KEY[layer.objectType] || 'discoverPage.panel.total', layer.objectType)}: ${count}`}
                                    checked={layer.enabled}
                                    onChange={(checked) => onToggleLayer(layer.id, checked)}
                                    color={layer.color || undefined}
                                    onColorChange={typeof onLayerColorChange === 'function'
                                        ? (color) => onLayerColorChange(layer.id, color)
                                        : undefined}
                                    tone={TYPE_TO_TONE[layer.objectType] || 'event'}
                                    actions={layerActions.length > 0 ? layerActions : undefined}
                                />
                            </SortableFilterRow>
                        );
                    })}
                </SortableContext>
            </DndContext>
            {hasAnyLayerEnabled && typeof totalCount === 'number' && (
                <p className="discover-panel__total" aria-live="polite">
                    {t('discoverPage.panel.totalOverall', {
                        count: totalCount,
                        defaultValue: 'Total markers: {{count}}',
                    })}
                    {showSelected && (
                        <span className="discover-panel__selected-total">
                            {t('discoverPage.panel.selectedOverall', {
                                count: selectedCount,
                                defaultValue: 'Selected: {{count}}',
                            })}
                        </span>
                    )}
                </p>
            )}
        </div>
    );

    return (
        <aside
            className={classNames('discover-panel', { 'discover-panel--minimized': isMinimized })}
            aria-label={t('discoverPage.panel.aria', 'Map controls and filters')}
        >
            {isMinimized ? (
                <div className="discover-panel__minimized-row">
                    {timestampSection}
                    {toggleButton}
                </div>
            ) : (
                toggleButton
            )}
            {/* Keep drafts and pending submissions alive while hiding controls. */}
            <ScrollArea
                className="discover-panel__content"
                role="presentation"
                style={isMinimized ? { display: 'none' } : undefined}
            >
                <div className="discover-panel__actions">
                    <DiscoverActionButton
                        onClick={onAddMarkerClick}
                        icon={<AddMarkerIcon />}
                        label={t('discoverPage.panel.addMarker', 'Add Marker')}
                    />
                    <DiscoverActionButton
                        onClick={onAddLayerClick}
                        icon={<AddLayerIcon />}
                        label={t('discoverPage.panel.addLayer', 'Add Layer')}
                        className="discover-action-button--layer"
                    />
                </div>

                {isComposerOpen && composerCard ? (
                    <div
                        key="discover-panel-composer"
                        className="discover-panel__swap-frame discover-panel__swap-frame--composer"
                    >
                        {composerCard}
                    </div>
                ) : (
                    <div
                        key="discover-panel-filters"
                        className="discover-panel__swap-frame discover-panel__swap-frame--filters"
                    >
                        {filtersCard}
                    </div>
                )}

                {!isMinimized && timestampSection}
                {children}
            </ScrollArea>
        </aside>
    );
}

DiscoverMapPanel.propTypes = {
    t: PropTypes.func.isRequired,
    layers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        objectType: PropTypes.string.isRequired,
        enabled: PropTypes.bool.isRequired,
        color: PropTypes.string,
    })).isRequired,
    layerCounts: PropTypes.objectOf(PropTypes.number),
    onToggleLayer: PropTypes.func.isRequired,
    onEditLayer: PropTypes.func,
    onDeleteLayer: PropTypes.func,
    onLayerColorChange: PropTypes.func,
    onAddMarkerClick: PropTypes.func.isRequired,
    onAddLayerClick: PropTypes.func.isRequired,
    onReorderLayers: PropTypes.func,
    totalCount: PropTypes.number,
    selectedCount: PropTypes.number,
    lastUpdatedMessage: PropTypes.string,
    onRefresh: PropTypes.func,
    isComposerOpen: PropTypes.bool,
    composerCard: PropTypes.node,
    children: PropTypes.node,
};
